// shop-frontend/src/pages/ShopShipping.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getShopOrders,
  getShippingStatus,
  getLabelPreflight,
  createShippingLabel,
  updateShopOrderStatus,
} from "../utils/api";

/* ─── helpers ──────────────────────────────────────────────────────────── */
// Paid by any rail and not shipped yet (transfer_pending / transferred are paid too).
const PAID_UNSHIPPED = ["paid", "paid_msc", "paid_mixed", "transferred", "transfer_pending", "transfer_blocked_currency", "processing"];
const isPaidUnshipped = (o) => PAID_UNSHIPPED.includes(String(o?.status || "").toLowerCase());

// The API returns the buyer address as shippingAddressSnapshot (street1/postalCode).
function addressOf(order) {
  return order?.shippingAddressSnapshot || order?.shippingAddress || order?.shipping?.toAddress || order?.shipping?.addressTo || null;
}
function formatAddress(a) {
  if (!a) return "";
  return [a.street1 || a.street, a.city, a.state, a.postalCode || a.zip, a.country].filter(Boolean).join(", ");
}

function statusBadge(status) {
  const map = {
    to_ship:          { label: "To Ship",      bg: "bg-blue-500/20",    text: "text-blue-400"    },
    pending:          { label: "To Ship",      bg: "bg-blue-500/20",    text: "text-blue-400"    },
    label_created:    { label: "Label Ready",  bg: "bg-purple-500/20",  text: "text-purple-400"  },
    shipped:          { label: "Shipped",      bg: "bg-emerald-500/20", text: "text-emerald-400" },
    in_transit:       { label: "In Transit",   bg: "bg-emerald-500/20", text: "text-emerald-400" },
    out_for_delivery: { label: "Out for Delivery", bg: "bg-emerald-500/20", text: "text-emerald-400" },
    delivered:        { label: "Delivered",    bg: "bg-purple-500/20",  text: "text-purple-400"  },
    exception:        { label: "Exception",    bg: "bg-red-500/20",     text: "text-red-400"     },
    cancelled:        { label: "Cancelled",    bg: "bg-red-500/20",     text: "text-red-400"     },
    error:            { label: "Error",        bg: "bg-red-500/20",     text: "text-red-400"     },
  };
  const s = map[String(status || "").toLowerCase()] || { label: status || "Pending", bg: "bg-gray-500/20", text: "text-gray-400" };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function orderStatusBadge(status) {
  const map = {
    paid:      { label: "Paid",      bg: "bg-emerald-500/20", text: "text-emerald-400" },
    shipped:   { label: "Shipped",   bg: "bg-blue-500/20",    text: "text-blue-400"    },
    completed: { label: "Delivered", bg: "bg-purple-500/20",  text: "text-purple-400"  },
    cancelled: { label: "Cancelled", bg: "bg-red-500/20",     text: "text-red-400"     },
    pending:   { label: "Pending",   bg: "bg-yellow-500/20",  text: "text-yellow-400"  },
  };
  const s = map[status] || (PAID_UNSHIPPED.includes(status) ? map.paid : null) || { label: status, bg: "bg-gray-500/20", text: "text-gray-400" };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

/* ─── Label Modal ───────────────────────────────────────────────────────── */
function LabelModal({ order, onClose, onLabeled }) {
  const nav = useNavigate();
  const [step, setStep]       = useState("preflight"); // preflight | creating | done | error
  const [preflightData, setPreflightData] = useState(null);
  const [labelData, setLabelData]         = useState(null);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    runPreflight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreflight = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await getLabelPreflight(order._id);
      setPreflightData(data);
      setStep("ready");
    } catch (e) {
      setError(e?.message || "Preflight check failed");
      setStep("error");
    } finally {
      setBusy(false);
    }
  };

  const handleCreateLabel = async () => {
    setBusy(true);
    setError("");
    setStep("creating");
    try {
      const data = await createShippingLabel(order._id);
      setLabelData(data);
      setStep("done");
      onLabeled?.();
    } catch (e) {
      setError(e?.message || "Failed to create label");
      setStep("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="lsa-root lsa-card rounded-xl border w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 lsa-border-b">
          <h3 className="text-base font-bold lsa-text-primary">Create Shipping Label</h3>
          <button onClick={onClose} className="lsa-text-muted hover:lsa-text-primary transition-colors text-lg leading-none">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Order summary */}
          <div className="text-sm lsa-text-muted">
            <span className="lsa-text-primary font-semibold">{order.productTitle || "Order"}</span>
            {" → "}
            <span className="cursor-pointer hover:text-purple-300 transition-colors" onClick={(e) => {
              e.stopPropagation();
              const uid = String(order.user?._id || order.user?.id || order.userId || order.buyerId || "");
              if (uid) nav(`/shop/customers?userId=${uid}`);
            }}>{order.buyerName || "Buyer"}</span>
          </div>
          {addressOf(order) && (
            <div className="text-xs lsa-text-muted">
              📍 {formatAddress(addressOf(order))}
            </div>
          )}

          {/* Preflight loading */}
          {step === "preflight" && (
            <div className="flex items-center gap-2 lsa-text-muted text-sm">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Checking order readiness…
            </div>
          )}

          {/* Ready */}
          {step === "ready" && preflightData?.ready && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-500 text-sm font-semibold">
                <span>✓</span> Ready to create label
              </div>
              {preflightData.note && (
                <p className="text-xs lsa-text-muted">{preflightData.note}</p>
              )}
              <button
                onClick={handleCreateLabel}
                disabled={busy}
                className="w-full py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm transition-colors disabled:opacity-50"
              >
                {busy ? "Creating…" : "Create Label & Purchase"}
              </button>
            </div>
          )}

          {/* Creating */}
          {step === "creating" && (
            <div className="flex items-center gap-2 lsa-text-muted text-sm">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Creating label with carrier…
            </div>
          )}

          {/* Done */}
          {step === "done" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-500 text-sm font-semibold">
                <span>✓</span> Label created successfully
              </div>
              {(labelData?.trackingNumber || labelData?.tracking?.trackingNumber) && (
                <div className="lsa-card rounded-lg p-3 border text-sm space-y-1">
                  <div className="lsa-text-muted text-xs">Tracking number</div>
                  <div className="lsa-text-primary font-mono font-semibold">
                    {labelData?.trackingNumber || labelData?.tracking?.trackingNumber}
                  </div>
                </div>
              )}
              {(labelData?.labelUrl || labelData?.label?.labelUrl) && (
                <a
                  href={labelData?.labelUrl || labelData?.label?.labelUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm text-center transition-colors"
                >
                  🖨 Print / Download Label
                </a>
              )}
            </div>
          )}

          {/* Error */}
          {step === "error" && (
            <div className="space-y-3">
              <p className="text-red-400 text-sm">{error}</p>
              <button
                onClick={runPreflight}
                className="w-full py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm transition-colors"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Tracking Row ──────────────────────────────────────────────────────── */
function TrackingInfo({ orderId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    getShippingStatus(orderId)
      .then(setData)
      .catch(() => {});
  }, [orderId]);

  if (!data?.trackingNumber) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
      <span className="lsa-text-muted">Tracking:</span>
      <span className="lsa-text-primary font-mono font-semibold">{data.trackingNumber}</span>
      {data.trackingStatus && (
        <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-semibold">
          {data.trackingStatus}
        </span>
      )}
      {data.labelUrl && (
        <a
          href={data.labelUrl}
          target="_blank"
          rel="noreferrer"
          className="text-blue-400 hover:text-blue-300 underline"
        >
          View Label
        </a>
      )}
    </div>
  );
}

/* ─── Order Card ────────────────────────────────────────────────────────── */
function ShippingOrderCard({ order, onLabeled, onMarkShipped }) {
  const nav = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [markingShipped, setMarkingShipped] = useState(false);

  const shippingStatus = order.shipping?.status || order.shipping?.shipstation?.status;
  const hasLabel       = !!(order.shipping?.shipstation?.labelId || order.shipping?.label?.labelId);
  const trackingNumber = order.shipping?.tracking?.trackingNumber
    || order.shipping?.shipstation?.trackingNumber
    || order.trackingNumber;

  const canCreateLabel = isPaidUnshipped(order) && !hasLabel;
  const canMarkShipped = (isPaidUnshipped(order) || hasLabel) && !["shipped", "completed", "cancelled", "refunded"].includes(String(order.status || "").toLowerCase());

  const handleMarkShipped = async () => {
    setMarkingShipped(true);
    try {
      await onMarkShipped(order._id);
    } finally {
      setMarkingShipped(false);
    }
  };

  return (
    <>
      <div className="lsa-root lsa-card rounded-xl border p-4 space-y-3 hover:shadow-md transition-shadow">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="lsa-text-primary font-semibold text-sm truncate">
              {order.productTitle || "Order"}
            </div>
            <div className="lsa-text-muted text-xs mt-0.5">
              #{String(order._id).slice(-8).toUpperCase()} · <span className="cursor-pointer hover:text-purple-300 transition-colors" onClick={(e) => {
                e.stopPropagation();
                const uid = String(order.user?._id || order.user?.id || order.userId || order.buyerId || "");
                if (uid) nav(`/shop/customers?userId=${uid}`);
              }}>{order.buyerName || "Buyer"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {orderStatusBadge(order.status)}
            {shippingStatus && statusBadge(shippingStatus)}
          </div>
        </div>

        {/* Shipping address */}
        {addressOf(order) && (
          <div className="text-xs lsa-text-muted">
            📍 {formatAddress(addressOf(order))}
          </div>
        )}

        {/* Selected rate */}
        {order.shipping?.buyerSelectedRate?.serviceCode && (
          <div className="text-xs lsa-text-muted">
            🚚 {order.shipping.buyerSelectedRate.carrierCode} — {order.shipping.buyerSelectedRate.serviceCode}
            {order.shipping.buyerSelectedRate.price != null && (
              <span className="ml-1 font-semibold lsa-text-primary">
                ${Number(order.shipping.buyerSelectedRate.price).toFixed(2)}
              </span>
            )}
          </div>
        )}

        {/* Tracking */}
        {trackingNumber ? (
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="lsa-text-muted">Tracking:</span>
            <span className="lsa-text-primary font-mono font-semibold">{trackingNumber}</span>
            {order.shipping?.shipstation?.trackingStatus && (
              <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-semibold text-xs">
                {order.shipping.shipstation.trackingStatus}
              </span>
            )}
            {(order.shipping?.shipstation?.labelUrl || order.shipping?.label?.labelUrl) && (
              <a
                href={order.shipping?.shipstation?.labelUrl || order.shipping?.label?.labelUrl}
                target="_blank"
                rel="noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                View Label
              </a>
            )}
          </div>
        ) : (
          hasLabel && <TrackingInfo orderId={order._id} />
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-1">
          {canCreateLabel && (
            <button
              onClick={() => setShowModal(true)}
              className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition-colors"
            >
              📦 Create Label
            </button>
          )}
          {hasLabel && !trackingNumber && (
            <button
              onClick={() => setShowModal(true)}
              className="px-3 py-1.5 rounded-lg bg-gray-600 hover:bg-gray-500 text-white text-xs font-semibold transition-colors"
            >
              🖨 View Label
            </button>
          )}
          {canMarkShipped && (
            <button
              onClick={handleMarkShipped}
              disabled={markingShipped}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
            >
              {markingShipped ? "Updating…" : "✓ Mark Shipped"}
            </button>
          )}
        </div>
      </div>

      {showModal && (
        <LabelModal
          order={order}
          onClose={() => setShowModal(false)}
          onLabeled={() => { setShowModal(false); onLabeled?.(); }}
        />
      )}
    </>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────── */
const TABS = [
  { key: "toship",  label: "To Ship",  filter: (o) => isPaidUnshipped(o) },
  { key: "shipped", label: "Shipped",  filter: (o) => o.status === "shipped" },
  { key: "all",     label: "All",      filter: () => true },
];

export default function ShopShipping() {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [tab, setTab]         = useState("toship");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getShopOrders();
      const list = Array.isArray(data) ? data : (data?.orders || []);
      // Only show orders with a shipping address (i.e. physical goods)
      setOrders(list.filter((o) => !!addressOf(o)));
    } catch (e) {
      setError(e?.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkShipped = async (orderId) => {
    await updateShopOrderStatus(orderId, "shipped");
    await load();
  };

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];
  const visible   = orders.filter(activeTab.filter);

  const counts = TABS.reduce((acc, t) => {
    acc[t.key] = orders.filter(t.filter).length;
    return acc;
  }, {});

  return (
    <div className="page-responsive space-y-6 max-w-[1200px] mx-auto min-h-screen" style={{ background: "var(--bg-primary)" }}>
      {/* Header */}
      <div className="rounded-xl p-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg">
        <h1 className="text-2xl font-bold mb-1">Shipping & Delivery</h1>
        <p className="text-sm opacity-85">
          Create shipping labels, track packages, and manage deliveries.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg" style={{ background: "var(--card-elevated)" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${
              tab === t.key
                ? "bg-blue-600 text-white shadow"
                : "text-gray-400 hover:text-white"
            }`}
          >
            {t.label}
            {counts[t.key] > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${tab === t.key ? "bg-white/20" : "bg-gray-600"}`}>
                {counts[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="lsa-root lsa-card rounded-xl border p-4 animate-pulse">
              <div className="h-4 w-48 rounded mb-2" style={{ background: "var(--lsa-row-hover)" }} />
              <div className="h-3 w-32 rounded" style={{ background: "var(--lsa-row-hover)" }} />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl p-6 border border-red-500/30 bg-red-500/10 text-red-400 text-sm">
          {error}
          <button onClick={load} className="ml-3 underline hover:no-underline">Retry</button>
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-xl p-10 text-center" style={{ background: "var(--card-bg)", border: "1px solid var(--border-color)" }}>
          <div className="text-4xl mb-3">📦</div>
          <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
            {tab === "toship" ? "No orders waiting to ship" : tab === "shipped" ? "No shipped orders yet" : "No shippable orders found"}
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
            Only orders with a shipping address appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((order) => (
            <ShippingOrderCard
              key={order._id}
              order={order}
              onLabeled={load}
              onMarkShipped={handleMarkShipped}
            />
          ))}
        </div>
      )}
    </div>
  );
}
